/**
 * Step 4's expiry picker (TO-125 REQ-105): a calendar date in the user's time
 * zone ↔ the instant the offer stores. A picked date means the end of that day
 * (23:59:59.999) where the user is; the picker offers today through today + 364
 * days, so a pick never exceeds the server's 365-day bound.
 */

const DAY_MS = 86_400_000
/** The last selectable day, counted from today. */
export const EXPIRY_MAX_DAYS_AHEAD = 364

/** The browser's IANA time zone (overridable in tests). */
export const browserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone

const partsInZone = (instant: Date, timeZone: string) => {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone,
		hourCycle: 'h23',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	}).formatToParts(instant)
	const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value)
	return {
		year: get('year'),
		month: get('month'),
		day: get('day'),
		hour: get('hour'),
		minute: get('minute'),
		second: get('second'),
	}
}

/** The zone's offset from UTC at `instant`, in ms (local − UTC). */
const offsetAt = (instant: Date, timeZone: string): number => {
	const p = partsInZone(instant, timeZone)
	const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
	return asUtc - (instant.getTime() - instant.getMilliseconds())
}

/** `yyyy-mm-dd` of `instant` in `timeZone`. */
export function dateInZone(instant: Date, timeZone: string): string {
	const p = partsInZone(instant, timeZone)
	return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

/** The instant `date` (`yyyy-mm-dd`) ends — 23:59:59.999 — in `timeZone`, across DST changes. */
export function endOfDayInZone(date: string, timeZone: string): Date {
	const [year, month, day] = date.split('-').map(Number) as [number, number, number]
	const wall = Date.UTC(year, month - 1, day, 23, 59, 59, 999)
	let instant = wall - offsetAt(new Date(wall), timeZone)
	// Re-read the offset at the candidate: the day may have changed offset (DST).
	instant = wall - offsetAt(new Date(instant), timeZone)
	return new Date(instant)
}

/** The picker's bounds: today and today + 364 days, as `yyyy-mm-dd` in `timeZone`. */
export function expiryRange(now: Date, timeZone: string): { min: string; max: string } {
	return {
		min: dateInZone(now, timeZone),
		max: dateInZone(new Date(now.getTime() + EXPIRY_MAX_DAYS_AHEAD * DAY_MS), timeZone),
	}
}
